// Inloggen, sessie, uitnodigingen en de keuze welke organisatie iemand ziet.
// Na een geldige sessie geeft deze module het stokje door aan de functie die
// app.js met koppelApp() registreert; zo hoeft auth.js App niet te kennen.
import { PRIMARY_ADMIN, APP_ENV } from './config.js';
import { maakClient, bewaarAnonKey } from './data/supabase.js';
import { t } from './lib/i18n.js';
import * as mijn from './pages/mijn.js';
import * as tweestap from './pages/tweestap.js';
import * as start from './pages/start.js';
import * as superbalk from './ui/superbalk.js';
import * as context from './lib/context.js';

let startApp = async () => { throw new Error('Geen app gekoppeld aan Auth'); };
export function koppelApp(fn) { startApp = fn; }

// The anon/public key is safe in frontend code — security is enforced by Supabase RLS.
// See: https://supabase.com/docs/guides/api/api-keys
export const Auth = (() => {
  let _sb = null;
  let _booted = false;

  async function init() {
    _sb = maakClient();
    if (!_sb) { showAnonKeyPrompt(); return; }
    toonMerknaam();

    // Check for existing session first (handles page refresh)
    const { data: { session: existingSession } } = await _sb.auth.getSession();
    if (existingSession) {
      _booted = true;
      try { await handleSession(existingSession); return; }
      catch(e) { _booted = false; }
    }

    // No existing session — listen for OAuth redirect callback
    _sb.auth.onAuthStateChange(async (event, session) => {
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
        if (_booted) return;
        _booted = true;
        try { await handleSession(session); }
        catch(e) { _booted = false; showAuthError('Login error: ' + e.message); showAuth(); }
      } else if (event === 'SIGNED_OUT') {
        _booted = false; showAuth();
      }
    });

    showAuth();
  }

  // Toegang komt uit organisation_members: daar staat bij welke organisaties
  // iemand hoort en met welke rol. De RLS-policies bepalen vervolgens wat die
  // rol werkelijk mag. Wie meer dan één ingang heeft (meerdere organisaties,
  // eigen gegevens, platformbeheer), komt op het startscherm.
  async function handleSession(session) {
    const email = (session.user.email || '').toLowerCase();
    const { data, error } = await _sb
      .from('organisation_members')
      .select('role, organisation_id')
      .eq('email', email)
      .eq('status', 'active')
      .order('created_at', { ascending: true });

    if (error) {
      // Nooit iemand buitensluiten door een storing: de primaire beheerder
      // komt er altijd in, de policies beschermen de gegevens alsnog.
      if (email === PRIMARY_ADMIN) { await bootApp(session, null); return; }
      showAuthError('Auth error: ' + error.message); showAuth(); return;
    }
    // Een link om een persoonsrecord te claimen gaat voor alles: ook een
    // beheerder kan ouder zijn en zijn eigen kind claimen.
    if (mijn.claimToken()) { await mijn.toonClaim(_sb, email); return; }

    const leden = data || [];
    const [platform, eigen, aanbiedingen] = await Promise.all([isPlatformbeheer(), mijn.laadEigenGegevens(_sb), telAanbiedingen()]);
    if (platform) await superbalk.toon(_sb, { email, env: APP_ENV });

    // Uitgenodigd als owner (nieuw toernooi of overdracht): naar het startscherm,
    // waar hij de voorwaarden aanvaardt. Ook als hij nog nergens lid is.
    if (aanbiedingen && !mijn.claimToken()) return kiesIngang(session, email, leden, platform, eigen, true);
    if (!leden.length && !platform) {
      // Geen lidmaatschap. Staat er een uitnodiging in de adresbalk, dan mag
      // deze persoon zichzelf toevoegen door de voorwaarden te aanvaarden.
      if (inviteToken()) { await toonUitnodiging(); return; }
      if (email === PRIMARY_ADMIN) { await bootApp(session, null); return; }
      // Lid of ouder zonder beheerrol: de eigen omgeving.
      if (eigen && await mijn.start(_sb)) return;
      showDenied(email);
      return;
    }
    // Owners, admins en platformbeheer met een tweede factor voeren eerst de code in.
    if (platform || leden.some(l => tweestap.BEHEERROLLEN.includes(l.role))) {
      const uitkomst = await tweestap.poort(_sb, () => kiesIngang(session, email, leden, platform, eigen));
      if (uitkomst === 'code') return;
      tweestapOntbreekt = uitkomst === 'geen';
    }
    await kiesIngang(session, email, leden, platform, eigen);
  }

  async function telAanbiedingen() {
    const { data, error } = await _sb.rpc('mijn_aanbiedingen');
    return !error && (data || []).length > 0;
  }

  async function isPlatformbeheer() {
    const { data, error } = await _sb.from('platform_admins').select('email').limit(1);
    return !error && !!data?.length;
  }

  // Waar deze persoon binnenkomt. Een eerdere keuze in dit tabblad gaat voor;
  // anders het startscherm bij meer dan één ingang, of meteen de enige.
  async function kiesIngang(session, email, leden, platform, eigen, aanbod = false) {
    context.volgGeschiedenis();
    context.markeer();
    const keuze = context.gekozen();
    if (aanbod) return start.toon(_sb, { email, platform, eigen });
    const aantal = leden.length + (platform ? 1 : 0) + (eigen ? 1 : 0);
    const naarStart = () => start.toon(_sb, { email, platform, eigen });
    if (keuze?.soort === 'start') return naarStart();
    if (keuze?.soort === 'mijn' && eigen) {
      return mijn.start(_sb, { terug: aantal > 1 ? () => context.naarStart() : null });
    }
    if (keuze?.soort === 'org') {
      const lid = leden.find(l => l.organisation_id === keuze.org);
      if (lid) return bootApp(session, lid);
      if (platform) return bootApp(session, { role: 'owner', organisation_id: keuze.org, via: 'platform' });
    }
    if (keuze?.soort === 'platform' && platform) return bootApp(session, leden[0] || null);
    if (aantal > 1 || !leden.length) return naarStart();
    return bootApp(session, leden[0]);
  }

  // ── UITNODIGINGEN ──
  // Het token staat in de adresbalk, maar de omleiding naar Google en terug
  // haalt de queryparameters weg. Daarom wordt hij bij het laden bewaard in
  // sessionStorage, die de omleiding wel overleeft en bij het sluiten van het
  // tabblad vanzelf verdwijnt.
  function bewaarInviteToken() {
    const uitUrl = new URLSearchParams(location.search).get('invite');
    if (uitUrl) {
      try { sessionStorage.setItem('pending_invite', uitUrl); } catch { /* geen opslag */ }
    }
  }

  function inviteToken() {
    const uitUrl = new URLSearchParams(location.search).get('invite');
    if (uitUrl) return uitUrl;
    try { return sessionStorage.getItem('pending_invite') || ''; } catch { return ''; }
  }

  function wisInviteToken() {
    try { sessionStorage.removeItem('pending_invite'); } catch { /* geen opslag */ }
  }

  async function inviteCall(action, payload = {}) {
    const { data, error } = await _sb.functions.invoke('invitations', {
      body: { action, token: inviteToken(), ...payload }
    });
    if (error) {
      let melding = error.message || 'Aanroep mislukt';
      try {
        const body = await error.context?.json?.();
        if (body?.error) melding = body.error;
      } catch { /* niet leesbaar */ }
      throw new Error(melding);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  }

  // De documenten die op het uitnodigingsscherm staan. Wat hier staat gaat mee
  // terug bij het aanvaarden, zodat de server kan controleren dat er niets is
  // overgeslagen of ondertussen gewijzigd.
  let inviteDocs = [];

  function inviteFout(msg) {
    document.getElementById('inviteLoading').style.display = 'none';
    document.getElementById('inviteBody').style.display = 'none';
    document.getElementById('inviteErrorMsg').textContent = msg;
    document.getElementById('inviteError').style.display = 'flex';
  }

  async function toonUitnodiging() {
    klaarMetLaden();
    document.getElementById('authScreen').classList.remove('active');
    document.getElementById('inviteScreen').classList.add('active');
    try {
      const info = await inviteCall('lookup');
      document.getElementById('inviteOrg').textContent = info.organisation;
      document.getElementById('inviteRole').textContent = info.role;
      // Welke documenten hier staan bepaalt de server aan de hand van de rol
      // uit de uitnodiging. Een owner gaat de overeenkomst met het platform
      // aan; wie er later bij komt aanvaardt alleen de gebruiksvoorwaarden.
      inviteDocs = Array.isArray(info.terms) ? info.terms : [info.terms];
      const soorten = {
        platform_terms: 'Overeenkomst met het platform',
        dpa: 'Verwerkersovereenkomst',
        user_terms: 'Gebruiksvoorwaarden',
        privacy_policy: 'Privacyverklaring',
      };
      document.getElementById('inviteDocs').innerHTML = inviteDocs.map((d, i) => `
        <div style="margin-bottom:14px">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px;flex-wrap:wrap">
            <span style="font-size:12px;font-weight:600">${soorten[d.kind] || d.kind}
              <span style="color:var(--text-3);font-weight:400">versie ${d.version} (${d.language}), geldig vanaf ${d.effective_from}</span></span>
            <a class="btn btn-default btn-sm" href="${d.content_url}" target="_blank" rel="noopener">
              <i class="ti ti-download"></i> Downloaden</a>
          </div>
          <div class="voorwaarden-tekst">${(d.content_text || 'De tekst van deze versie staat alleen in het document. Download het hierboven en lees het voordat je akkoord gaat.').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))}</div>
          <label style="display:flex;gap:9px;align-items:flex-start;font-size:13px;cursor:pointer;margin-top:8px">
            <input type="checkbox" class="invite-doc-cb" data-doc="${d.id}" style="margin-top:3px;flex-shrink:0">
            <span>Ik heb ${(soorten[d.kind] || d.kind).toLowerCase()} versie ${d.version} gelezen en ga daarmee akkoord.</span>
          </label>
        </div>`).join('');

      // Een owner verklaart iets zwaarders dan een editor. Dat hoort ook in de
      // tekst van het vinkje te staan, anders tekent iedereen hetzelfde.
      const bevoegdheid = document.getElementById('inviteAuthorityTekst');
      if (bevoegdheid) {
        bevoegdheid.textContent = info.role === 'owner'
          ? 'Ik ben bevoegd om namens deze organisatie deze overeenkomst aan te gaan en de organisatie hieraan te binden.'
          : 'Ik ben bevoegd om namens deze organisatie akkoord te geven.';
      }
      document.getElementById('inviteLoading').style.display = 'none';
      document.getElementById('inviteBody').style.display = 'block';
    } catch (e) {
      inviteFout(e.message);
    }
  }

  async function acceptInvite() {
    const naam = document.getElementById('inviteName').value.trim();
    const functie = document.getElementById('inviteJob').value.trim();
    const vinkjes = Array.from(document.querySelectorAll('.invite-doc-cb'));
    const bevoegd = document.getElementById('inviteAuthority').checked;
    if (!naam || !functie) { inviteFout('Vul je naam en functie in.'); return; }
    if (!vinkjes.length) { inviteFout('Er zijn geen voorwaarden geladen. Herlaad de pagina.'); return; }
    if (!vinkjes.every((v) => v.checked)) { inviteFout('Alle voorwaarden moeten zijn aangevinkt.'); return; }
    if (!bevoegd) { inviteFout('Bevestig dat je bevoegd bent.'); return; }

    const btn = document.getElementById('inviteBtn');
    btn.disabled = true;
    btn.innerHTML = '<div class="spinner"></div> Bezig…';
    try {
      await inviteCall('accept', {
        name: naam, job_title: functie, authority_confirmed: true,
        accepted_document_ids: vinkjes.map((v) => v.dataset.doc),
      });
      wisInviteToken();
      // Zonder token in de adresbalk herladen, anders blijft het scherm terugkomen.
      location.replace(location.pathname);
    } catch (e) {
      inviteFout(e.message);
      btn.disabled = false;
      btn.innerHTML = '<i class="ti ti-check"></i> Aanvaarden en doorgaan';
    }
  }

  let tweestapOntbreekt = false;

  async function bootApp(session, lidmaatschap) {
    wisInviteToken();
    try {
      document.getElementById('authScreen').classList.remove('active');
      document.getElementById('deniedScreen').classList.remove('active');
      document.getElementById('appRoot').style.display = 'flex';
      const user = session.user;
      const name = user.user_metadata?.full_name || user.email;
      const avatar = user.user_metadata?.avatar_url;
      document.getElementById('sidebarName').textContent = name;
      const rolLabels = { owner:'Owner', admin:'Admin', editor:'Editor', viewer:'Viewer' };
      document.getElementById('sidebarRole').textContent = lidmaatschap?.via === 'platform' ? t('start.toegang.platform')
        : rolLabels[lidmaatschap?.role] || (user.email === PRIMARY_ADMIN ? 'Primary admin' : 'Admin');
      const naarStartKnop = document.getElementById('naarStart');
      if (naarStartKnop) naarStartKnop.onclick = () => context.naarStart();
      const avatarEl = document.getElementById('sidebarAvatar');
      if (avatar) avatarEl.innerHTML = `<img src="${avatar}" alt="">`;
      else avatarEl.textContent = name.charAt(0).toUpperCase();
      await startApp(_sb, user.email, lidmaatschap || null);
      superbalk.ververs();
      // Pas nu menu, rechten en pagina klaar zijn, wordt de app zichtbaar.
      document.getElementById('appRoot').classList.remove('app-laden');
      klaarMetLaden();
      if (tweestapOntbreekt) {
        const b = document.getElementById('bannerContainer');
        if (b) b.insertAdjacentHTML('afterbegin',
          `<div class="banner banner-amber"><i class="ti ti-shield-exclamation"></i><span>${t('tweestap.melding')}</span></div>`);
      }
      // Wie naast beheerder ook lid of ouder is, krijgt een knop naar de eigen omgeving.
      if (await mijn.laadEigenGegevens(_sb)) {
        const knop = document.getElementById('naarMijnGegevens');
        if (knop) {
          knop.hidden = false;
          knop.onclick = () => context.kies({ soort: 'mijn' });
        }
      }
    } catch(e) {
      console.error('bootApp error:', e);
      klaarMetLaden();
      document.getElementById('appRoot').style.display = 'none';
      showAuthError('App failed to load: ' + e.message);
      document.getElementById('authScreen').classList.add('active');
    }
  }

  function saveAnonKey() {
    const key = document.getElementById('anonKeyInput')?.value.trim();
    if (!key || key.length < 20) { showAuthError('Invalid key'); return; }
    bewaarAnonKey(key);
    location.reload();
  }

  async function signInGoogle() {
    if (!_sb) { showAuthError('Not connected to database.'); return; }
    const btn = document.querySelector('.btn-google');
    if (btn) { btn.disabled = true; btn.innerHTML = '<i class="ti ti-loader-2" style="animation:spin 1s linear infinite"></i> Signing in…'; }
    const { error } = await _sb.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + window.location.pathname + window.location.search }
    });
    if (error) {
      showAuthError(error.message);
      if (btn) { btn.disabled = false; btn.innerHTML = '<img src="https://www.google.com/favicon.ico" style="width:18px;height:18px"> Continue with Google'; }
    }
  }

  // Inloggen met een link per e-mail. Voor leden en ouders die geen
  // Google-account willen gebruiken. De link komt terug op dezelfde pagina,
  // inclusief een eventuele claim in de adresbalk.
  async function stuurInloglink() {
    if (!_sb) { showAuthError('Not connected to database.'); return; }
    const veld = document.getElementById('inlogEmail');
    const email = (veld?.value || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email)) { showAuthError(t('auth.link.ongeldig')); return; }
    const { error } = await _sb.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin + window.location.pathname + window.location.search },
    });
    if (error) { showAuthError(t('alg.fout', { melding: error.message })); return; }
    const el = document.getElementById('inlogLinkStatus');
    if (el) el.innerHTML = `<div class="banner banner-green" style="margin-top:10px"><i class="ti ti-mail-check"></i><span>${t('auth.link.verstuurd', { email: email.replace(/[<>&]/g, '') })}</span></div>`;
  }

  async function signOut() {
    context.wis();
    if (_sb) await _sb.auth.signOut();
    // Intentionally keep anon key in localStorage — it is a public key
    location.reload();
  }

  // De merknaam van het platform staat in de database, zodat hij op één plek
  // te wijzigen is. Het inlogscherm heeft nog geen sessie, dus hij komt uit een
  // view die alleen die ene kolom vrijgeeft.
  async function toonMerknaam() {
    let naam = 'CRM';
    try {
      const { data } = await _sb.from('platform_public').select('platform_name').maybeSingle();
      if (data?.platform_name) naam = data.platform_name;
    } catch { /* niet bereikbaar, dan de neutrale naam */ }
    const titel = document.getElementById('brandTitle');
    const sub = document.getElementById('brandSub');
    if (titel) titel.textContent = naam;
    if (sub) sub.textContent = naam;
    document.title = naam;
    window.__merknaam = naam;
  }

  function showAuth() {
    klaarMetLaden();
    document.getElementById('authScreen').classList.add('active');
  }
  function showDenied(email) {
    klaarMetLaden();
    document.getElementById('authScreen').classList.remove('active');
    const d = document.getElementById('deniedScreen');
    d.classList.add('active');
    const el = d.querySelector('.denied-email');
    if (el) el.textContent = email;
  }
  function showAuthError(msg) {
    let el = document.getElementById('authError');
    if (!el) {
      el = document.createElement('div');
      el.id = 'authError';
      el.style.cssText = 'margin-top:12px;padding:10px 14px;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;font-size:13px;color:#b91c1c;text-align:center';
      document.querySelector('.auth-card')?.appendChild(el);
    }
    el.textContent = msg;
  }
  function showAnonKeyPrompt() {
    const card = document.querySelector('.auth-card');
    if (!card) return;
    card.innerHTML = `
      <h2>Database setup</h2>
      <p style="font-size:13px;color:#6b7280;margin-bottom:16px">
        One-time setup. Enter your Supabase anon public key.<br>
        Find it at <strong>supabase.com &rarr; Settings &rarr; API &rarr; anon public</strong>.
      </p>
      <input id="anonKeyInput" type="password" placeholder="eyJ..."
        style="width:100%;padding:9px 12px;border:1px solid #e5e7eb;border-radius:8px;font-size:13px;margin-bottom:10px;outline:none;box-sizing:border-box">
      <button onclick="Auth.saveAnonKey()"
        style="width:100%;padding:11px;background:#0f6674;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer">
        Save &amp; continue
      </button>`;
    showAuth();
  }
  function getSb() { return _sb; }

  // Het laadscherm weg zodra er een scherm is om te tonen. Hangt er iets, dan
  // na tien seconden toch het inlogscherm: nooit een eindeloos laadscherm.
  function klaarMetLaden() { document.body.classList.remove('laden'); }
  setTimeout(() => {
    if (!document.body.classList.contains('laden')) return;
    klaarMetLaden();
    const zichtbaar = ['appRoot', 'startRoot', 'mijnRoot'].some(id => {
      const el = document.getElementById(id); return el && el.style.display && el.style.display !== 'none';
    });
    if (!zichtbaar) document.getElementById('authScreen')?.classList.add('active');
  }, 10000);

  // Meteen bij het laden, niet pas in init(). Als de Supabase-bibliotheek
  // traag of niet laadt, is het token anders al uit de adresbalk verdwenen
  // voordat we hem konden bewaren.
  bewaarInviteToken();
  mijn.bewaarClaimToken();

  return { init, signInGoogle, stuurInloglink, signOut, saveAnonKey, getSb, acceptInvite };
})();
